import { useCallback, useEffect, useRef } from "react";

import { usePlayground } from "../../playground-store";

import { Message } from "@/components/Messaging/types";
import { useChannel } from "@/components/Messaging/useChannel";
import { useUser } from "@/context/user-context";

/**
 * Wraps useChannel with WS message buffering for the Playground.
 *
 * On slow networks, WS messages can arrive before the REST response assigns
 * request_ids. This hook buffers those early messages and replays them once
 * the request_id becomes valid (after assignSetRequestId runs).
 *
 * Use this instead of useChannel for all Playground WS listeners.
 */
const usePlaygroundChannel = (event: string, callback: (payload: Message) => void) => {
  const { user } = useUser();
  const playground = usePlayground();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const bufferedCallback = useCallback(
    (payload: Message) => {
      const { individual_run_request_id } = JSON.parse(payload.data);
      if (!playground.isRequestValid(individual_run_request_id)) {
        playground.bufferMessage(individual_run_request_id, event, payload);
        return;
      }
      callbackRef.current(payload);
    },
    [playground, event],
  );

  useEffect(() => {
    playground.registerWsHandler(event, bufferedCallback as (payload: unknown) => void);
    return () => playground.unregisterWsHandler(event);
  }, [event, playground, bufferedCallback]);

  useChannel(`user:${user.id}`, event, bufferedCallback);
};

export { usePlaygroundChannel };
